import { IconFont } from '@/components/IconFont'
import OrbitAvatar from '@/components/OrbitAvatar'
import TermsSelect from '@/components/TermsSelect'
import { projectContext } from '@/layouts/MainLayout'
import { invitation } from '@/services/member/member'
import { queryUsersAndOrgs } from '@/services/user/user.service'
import { projectAccountTypes } from '@/utils/constants'
import { ModalForm } from '@ant-design/pro-form'
import { useRequest } from 'ahooks'
import { AutoComplete, Form, Input, Select, Space } from 'antd'
import { memo, useContext, useMemo, useState } from 'react'

interface InvitationMemberProps {
  trigger: JSX.Element
  onSuccess?: (link: string) => void
}

export default memo<InvitationMemberProps>(function InvitationMember(props) {
  const { projectId, projectData } = useContext(projectContext)
  const [form] = Form.useForm()
  const { trigger, onSuccess, ...other } = props
  const [options, setOptions] = useState<any[]>([])
  const [selectedOption, setSelectedOption] = useState<any>(null)
  const { run: runQuery } = useRequest(queryUsersAndOrgs, {
    manual: true,
    debounceWait: 300,
    onSuccess: (data) => {
      const options: any[] = []
      if (data.users?.length > 0) {
        options.push({
          label: 'Users',
          options: data.users.map((item) => ({
            type: 'user',
            value: item.email,
            label: (
              <Space>
                <OrbitAvatar avatar={item.avatar} name={item.fullName} />
                <span>{item.email}</span>
              </Space>
            )
          }))
        })
      }
      if (data.organizations?.length > 0) {
        options.push({
          label: 'Organizations',
          options: data.organizations.map((item) => ({
            type: 'organization',
            id: item.id,
            value: `${item.name} (${item.contactEmail})`,
            label: (
              <Space>
                <OrbitAvatar avatar={item.avatar} name={item.name} />
                <span>
                  {item.name} ({item.contactEmail})
                </span>
              </Space>
            )
          }))
        })
      }
      setOptions(options)
    }
  })

  const accountTypes = useMemo(() => {
    return projectAccountTypes.filter(item => item.value !== 'OWNER')
  }, [])

  return (
    <ModalForm
      {...other}
      form={form}
      trigger={trigger}
      width={500}
      title={`Invite a member to ${projectData.name}`}
      modalProps={{
        maskClosable: false
      }}
      onVisibleChange={(visible: boolean) => {
        visible && form.resetFields()
      }}
      onFinish={async (values) => {
        const reqBody = { ...values }
        if (selectedOption?.type === 'organization') {
          delete reqBody.email
          Object.assign(reqBody, { memberOrgId: selectedOption.id })
        }
        const data = await invitation({
          ...reqBody,
          projectId: Number(projectId)
        })
        onSuccess?.(data.fullUrl)
        return true
      }}
    >
      <Form.Item label='Search users by name, email' name='email'>
        <AutoComplete
          options={options}
          onSearch={(value: string) => {
            setSelectedOption(null)
            runQuery(value)
          }}
          onSelect={(_, option) => {
            setSelectedOption(option)
          }}
        >
          <Input type='email' prefix={<IconFont type={'icon-search'} />} />
        </AutoComplete>
      </Form.Item>

      <Form.Item name='accountType' label="Account Type" required>
        <Select options={accountTypes} />
      </Form.Item>

      <Form.Item label='Terms' name='termsId' rules={[{ required: true, message: 'Please select terms' }]}>
        <TermsSelect orgId={projectData.orgId} />
      </Form.Item>
    </ModalForm>
  )
})
